/**
 * i18n bootstrap (ADR-0012 §1–3).
 *
 * Three rules are encoded here:
 *
 * 1. **Persian is the default**, and the fallback, so a missing English key shows Persian text rather than a raw
 *    key name.
 * 2. **The document direction, language and font class are updated in one place.** Switching locale cannot leave
 *    the app in a half-mirrored state, and `dir` is set before React renders (see `main.tsx`).
 * 3. **Interpolation is escaped**, because prompt previews and template text are user content and a Persian
 *    layout is no excuse for an injection point.
 */

import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import en from '@/lib/i18n/en.json';
import fa from '@/lib/i18n/fa.json';
import { type AppLocale } from '@/stores/uiStore';

/** Translation namespaces; a flat namespace keeps keys short and reviewable. */
export const DEFAULT_NAMESPACE = 'translation';

/** The direction a locale renders in. */
export function directionFor(locale: AppLocale): 'rtl' | 'ltr' {
  return locale === 'fa' ? 'rtl' : 'ltr';
}

/** Normalizes any stored or detected tag (`fa-IR`, `en-GB`) onto a supported locale. */
export function normalizeLocale(input: string | undefined | null): AppLocale {
  return input?.toLowerCase().startsWith('en') ? 'en' : 'fa';
}

/**
 * Applies a locale to the document element.
 *
 * Called both at bootstrap and on every locale change, so the `dir`, `lang` and font-family state can never drift
 * apart from the store.
 *
 * @param locale Locale to apply.
 */
export function applyDocumentLocale(locale: AppLocale): void {
  const root = document.documentElement;

  root.lang = locale === 'fa' ? 'fa-IR' : 'en-US';
  root.dir = directionFor(locale);
}

/** The locale resolved from persisted UI state, the browser or the fallback, in that order. */
export function resolveInitialLocale(): AppLocale {
  const stored = window.localStorage.getItem('prompt-forge.ui');

  if (stored) {
    try {
      const parsed = JSON.parse(stored) as { state?: { locale?: string } };
      return normalizeLocale(parsed.state?.locale);
    } catch {
      // A corrupt entry must never prevent the app from booting; the fallback locale takes over.
    }
  }

  return normalizeLocale(window.navigator.language);
}

/** Applies locale and direction together; used by `useLocale()` on every change. */
export function setLocale(locale: AppLocale): Promise<unknown> {
  applyDocumentLocale(locale);

  return i18n.changeLanguage(locale);
}

export const i18nResources = {
  fa: { [DEFAULT_NAMESPACE]: fa },
  en: { [DEFAULT_NAMESPACE]: en },
} as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: i18nResources,
    fallbackLng: 'fa',
    supportedLngs: ['fa', 'en'],
    load: 'languageOnly',
    defaultNS: DEFAULT_NAMESPACE,
    ns: [DEFAULT_NAMESPACE],
    interpolation: {
      // React escapes rendered strings already; disabling i18next's escaping keeps "{count}" readable in RTL.
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'prompt-forge.ui',
      caches: [],
    },
    returnNull: false,
  });

export default i18n;
